'use client';

import LogoutButton from '@/components/LogoutButton';
import Sidebar from '@/components/Sidebar';
import SignInModal from '@/components/SignInModal';
import { useUser } from '@/hooks/useUser';
import theme from '@/theme';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import { AppBar, Box, Button, Container, CssBaseline, Divider, Drawer, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import { Analytics } from '@vercel/analytics/react';
import Link from 'next/link';
import { useState } from 'react';
import Footer from './Footer';
export const BaseTemplate = ({
  leftNav,
  children,
  maxWidth = 'lg'
}: {
  leftNav?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'xl' | 'lg' | 'md' | 'sm' | false;
}) => {
  const {
    data: user
  } = useUser();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Define sidebar width for consistency
  const SIDEBAR_WIDTH = 240;
  const toggleMobileSidebar = () => setMobileSidebarOpen(prev => !prev);
  const renderSignedInButtons = () => <Stack direction="row" spacing={1} sx={{
    display: {
      xs: 'none',
      sm: 'flex'
    }
  }} data-sentry-element="Stack" data-sentry-component="renderSignedInButtons" data-sentry-source-file="BaseTemplate.tsx">
      <LogoutButton data-sentry-element="LogoutButton" data-sentry-source-file="BaseTemplate.tsx" />
    </Stack>;
  const renderSignInButtons = () => <SignInModal data-sentry-element="SignInModal" data-sentry-component="renderSignInButtons" data-sentry-source-file="BaseTemplate.tsx" />;
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
  }} data-sentry-element="Box" data-sentry-component="BaseTemplate" data-sentry-source-file="BaseTemplate.tsx">
      <CssBaseline data-sentry-element="CssBaseline" data-sentry-source-file="BaseTemplate.tsx" />
      <Analytics data-sentry-element="Analytics" data-sentry-source-file="BaseTemplate.tsx" />

      {/* App Bar - Fixed at the top */}
      <AppBar position="fixed" elevation={0} sx={{
      bgcolor: 'background.paper',
      borderBottom: `1px solid ${theme.palette.divider}`,
      zIndex: theme => theme.zIndex.drawer + 1,
      borderRadius: 0
    }} data-sentry-element="AppBar" data-sentry-source-file="BaseTemplate.tsx">
        <Toolbar sx={{
        minHeight: 64,
        px: {
          xs: 2,
          md: 4
        }
      }} data-sentry-element="Toolbar" data-sentry-source-file="BaseTemplate.tsx">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1
        }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
            {user && <IconButton onClick={toggleMobileSidebar} edge="start" sx={{
            mr: 2,
            display: {
              md: 'none'
            }
          }}>
                <MenuIcon />
              </IconButton>}

            {/* Brand */}
            <Link href="/" passHref style={{
            textDecoration: 'none'
          }} data-sentry-element="Link" data-sentry-source-file="BaseTemplate.tsx">
              <Box sx={{
              'display': 'flex',
              'alignItems': 'center',
              'cursor': 'pointer',
              '&:hover .brand-text': {
                color: 'primary.main'
              }
            }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
                <SchoolIcon sx={{
                fontSize: 32,
                color: 'primary.main',
                mr: 1
              }} data-sentry-element="SchoolIcon" data-sentry-source-file="BaseTemplate.tsx" />
                <Typography className="brand-text" variant="h6" component="div" sx={{
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'text.primary',
                transition: 'color 0.2s',
                fontSize: {
                  xs: '1.25rem',
                  md: '1.5rem'
                }
              }} data-sentry-element="Typography" data-sentry-source-file="BaseTemplate.tsx">
                  CertResource
                  <Box component="span" sx={{
                  fontWeight: 400,
                  color: 'text.secondary',
                  ml: 0.5
                }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
                    .com
                  </Box>
                </Typography>
              </Box>
            </Link>
          </Box>

          {/* Nav Links */}
          <Box sx={{
          display: {
            xs: 'none',
            md: 'flex'
          },
          gap: 2,
          mr: 2
        }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
            {leftNav ?? <>
                <Button color="inherit" sx={{
              fontWeight: 600
            }}>
                  Cert Exams
                </Button>
                <Button color="inherit" sx={{
              fontWeight: 600
            }}>
                  Pricing
                </Button>
                <Button color="inherit" sx={{
              fontWeight: 600
            }}>
                  Blog
                </Button>
              </>}
          </Box>

          {/* Auth Buttons */}
          {user ? renderSignedInButtons() : renderSignInButtons()}
        </Toolbar>
      </AppBar>

      {/* Main container with sidebar and content */}
      <Box sx={{
      display: 'flex',
      flexGrow: 1,
      pt: '64px' // Offset for fixed AppBar
    }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
        {/* Desktop Sidebar - Fixed position */}
        {user && <Box component="nav" sx={{
        width: {
          md: SIDEBAR_WIDTH
        },
        flexShrink: 0,
        display: {
          xs: 'none',
          md: 'block'
        }
      }}>
            <Drawer variant="permanent" open sx={{
          'display': {
            xs: 'none',
            md: 'block'
          },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            pt: '64px' // Space for AppBar
          }
        }}>
              <Box sx={{
            padding: '0px 8px 0px 8px',
            overflow: 'auto'
          }}>
                <Sidebar />
              </Box>
            </Drawer>
          </Box>}

        {/* Mobile Drawer - Temporary */}
        {user && <Drawer anchor="left" open={mobileSidebarOpen} onClose={toggleMobileSidebar} ModalProps={{
        keepMounted: true
      }} slotProps={{
        paper: {
          sx: {
            width: {
              xs: '80%',
              sm: SIDEBAR_WIDTH
            },
            bgcolor: 'background.paper',
            p: 2,
            pt: '64px' // Space for AppBar
          }
        }
      }}>
            <Box sx={{
          overflow: 'auto'
        }}>
              <Sidebar closeCB={() => {
            toggleMobileSidebar();
          }} />
              <Divider sx={{
            my: 2
          }} />
              <LogoutButton />
            </Box>
          </Drawer>}

        {/* Main content */}
        <Box component="main" data-test-id="main-content" sx={{
        flexGrow: 1,
        p: {
          xs: 0,
          sm: 1
        },
        width: {
          md: `calc(100% - ${user ? SIDEBAR_WIDTH : 0}px)`
        },
        overflow: 'auto'
      }} data-sentry-element="Box" data-sentry-source-file="BaseTemplate.tsx">
          <Container maxWidth={maxWidth} data-test-id="container" sx={{
          pt: 1,
          pb: 8 // Add padding at bottom to avoid content being hidden by footer
        }} data-sentry-element="Container" data-sentry-source-file="BaseTemplate.tsx">
            {children}
          </Container>
        </Box>
      </Box>

      {/* Footer - Always at the bottom */}
      <Footer data-sentry-element="Footer" data-sentry-source-file="BaseTemplate.tsx" />
    </Box>;
};